import React, { useMemo, useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import projects from "../data/projectsData";
import Seo from "../components/Seo";
import "../components/Section.css";
import "../components/Projects.css";
import { fadeUp, staggerContainer, staggerItem, easing } from "../utils/motionVariants";

export default function ProjectsPage({ onProjectSelect = () => {} }) {
  const categories = ["Web Development", "App Development", "AI/ML", "WP Plugins"];
  const [activeCategory, setActiveCategory] = useState(categories[0]);

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      if (Array.isArray(project.category)) {
        return project.category.includes(activeCategory);
      }
      return project.category === activeCategory;
    });
  }, [activeCategory]);

  return (
    <section id="projects" className="section section-mist">
      <Seo
        title="Projects"
        path="/portfolio"
        description={`Selected projects by Hirun Kolongahapitiya - ${projects.length}+ web, mobile, and AI builds spanning fintech, edtech, real estate, streaming, and automation.`}
      />
      <motion.div
        className="section-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <h2>Creative Showcase</h2>
        <div className="section-underline" />
      </motion.div>

      <LayoutGroup>
        <motion.div
          className="project-tabs"
          role="tablist"
          aria-label="Project categories"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4, ease: easing }}
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat}
              className={activeCategory === cat ? "active" : ""}
              onClick={() => setActiveCategory(cat)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              style={{ position: "relative" }}
            >
              {activeCategory === cat && (
                <motion.span
                  layoutId="active-tab-pill"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    background: "rgba(99, 102, 241, 0.12)",
                    zIndex: -1,
                  }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {cat}
            </motion.button>
          ))}
        </motion.div>
      </LayoutGroup>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          className="projects-grid"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
        >
          {filteredProjects.map((project) => (
            <motion.article
              key={project.slug}
              className="project-card"
              variants={staggerItem}
              whileHover={{ y: -10, scale: 1.02, boxShadow: "0 24px 50px rgba(15, 23, 42, 0.16)" }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.25, ease: easing }}
              onClick={() => onProjectSelect(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onProjectSelect(project);
                }
              }}
            >
              {project.cover && (
                <motion.img
                  src={project.cover}
                  alt={project.title}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4, ease: easing }}
                />
              )}
              <h3>{project.title}</h3>
              <p className="muted">{project.oneLiner}</p>
              <span className="muted">
                {project.timeframe.start}
                {project.timeframe.end ? ` - ${project.timeframe.end}` : ""} • {project.timeframe.status}
              </span>
            </motion.article>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
